import { Grid2 as Grid, Typography, useTheme } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe, loadStripe } from '@stripe/stripe-js';
import StripeCardFields from './StripeCardFields';

// Loaded on demand rather than with the rest of the booking form: most customers are
// approved by eWay first time and never reach this component, so there's no reason to
// pull Stripe.js into every checkout. Memoised so a customer who declines twice doesn't
// fetch the script twice, and guarded for SSR/prerender where there is no document to
// attach the script to.
let stripePromise: Promise<Stripe | null> | undefined;

const getStripe = () => {
    if (typeof window === 'undefined') {
        return undefined;
    }
    // Chosen at build time, mirroring how apiURL is selected in BookingStore and how the
    // server picks its matching secret key from NODE_ENV. Keeping the two keys under
    // separate names means a local `npm run build` can't bake the sandbox key into a
    // production bundle. A live card token is rejected by a sandbox secret key and vice
    // versa, so a mismatch fails loudly rather than taking a payment against the wrong
    // account.
    const keyName = import.meta.env.PROD ? 'VITE_STRIPE_PRODUCTION_KEY' : 'VITE_STRIPE_PUBLISHABLE_KEY';
    const publishableKey = import.meta.env.PROD
        ? import.meta.env.VITE_STRIPE_PRODUCTION_KEY
        : import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
        console.error(`[payment] ${keyName} is not set; the payment fallback is unavailable.`);
        return undefined;
    }
    if (!stripePromise) {
        stripePromise = loadStripe(publishableKey);
    }
    return stripePromise;
};

const StripeFallbackForm = () => {
    const theme = useTheme();
    const stripe = getStripe();

    // Without a key there is no second attempt to offer, so fall back to telling the
    // customer what actually happened rather than rendering a form that cannot submit.
    if (!stripe) {
        return (
            <Grid container>
                <Typography variant="h6" style={{ color: 'red', paddingBottom: 10, width: '100%' }}>
                    Payment Declined
                </Typography>
                <Typography style={{ fontSize: 16, color: theme.palette.text.soft }}>
                    Your bank declined the payment and no charge has been made. Please contact us and we will happily
                    book your tour over the phone.
                </Typography>
            </Grid>
        );
    }

    return (
        <Elements stripe={stripe} options={{ locale: 'en-AU' }}>
            <StripeCardFields />
        </Elements>
    );
};

export default StripeFallbackForm;
